import { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

/**
 * Modale de suppression d'un dossier — DOUBLE CONFIRMATION.
 * L'utilisateur (admin_full uniquement) doit taper EXACTEMENT le nom du dossier
 * pour activer le bouton "Supprimer". Le nom saisi est ensuite envoyé au
 * backend qui refait la vérification (défense en profondeur).
 *
 * Props :
 *  - open (bool)
 *  - onOpenChange (fn)
 *  - prospectName (string) : nom canonique attendu (raison sociale ou "prénom nom")
 *  - onConfirm (fn(confirmName)) : appelé avec le nom exact tapé
 *  - processing (bool)
 *  - mode : 'trash' (défaut, soft delete → corbeille) ou 'permanent' (hard delete)
 */
export function DeleteProspectDialog({
    open,
    onOpenChange,
    prospectName,
    onConfirm,
    processing = false,
    mode = 'trash',
}) {
    const [typed, setTyped] = useState('');

    useEffect(() => {
        if (!open) setTyped('');
    }, [open]);

    const isPermanent = mode === 'permanent';
    const expected = (prospectName || '').trim();
    const matches = useMemo(
        () => expected.length > 0 && typed.trim().toLowerCase() === expected.toLowerCase(),
        [typed, expected]
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="delete-prospect-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                        {isPermanent ? 'Suppression définitive' : 'Mettre à la corbeille'}
                    </DialogTitle>
                    <DialogDescription className="pt-2 space-y-3">
                        <span className="block">
                            {isPermanent ? (
                                <>
                                    Vous êtes sur le point de <strong>supprimer définitivement</strong> ce dossier
                                    ainsi que toutes ses données liées (documents, commissions, tâches).
                                    <strong className="block text-red-700 mt-2">Cette action est irréversible.</strong>
                                </>
                            ) : (
                                <>
                                    Vous êtes sur le point de <strong>mettre à la corbeille</strong> ce dossier.
                                    Il pourra être restauré depuis <em>Paramètres → Maintenance → Corbeille</em>
                                    par un Administrateur Full.
                                </>
                            )}
                        </span>
                        <span className="block font-semibold text-slate-900 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                            {expected || 'Dossier sans nom'}
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2">
                    <Label htmlFor="delete-prospect-confirm-input" className="text-sm">
                        Pour confirmer, tapez le nom exact du dossier :
                    </Label>
                    <Input
                        id="delete-prospect-confirm-input"
                        data-testid="delete-prospect-confirm-input"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={expected}
                        autoComplete="off"
                        disabled={processing}
                    />
                    <p className="text-xs text-slate-500">
                        La casse n&apos;a pas d&apos;importance, mais l&apos;orthographe doit être identique.
                    </p>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={processing}
                        data-testid="delete-prospect-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={!matches || processing}
                        onClick={() => onConfirm(typed.trim())}
                        data-testid="delete-prospect-confirm-btn"
                    >
                        {processing
                            ? 'Suppression…'
                            : isPermanent
                              ? 'Supprimer définitivement'
                              : 'Mettre à la corbeille'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteProspectDialog;
